import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useUser } from 'context/UserContext';
import useSignout from '../../../hooks/Authentication/useSignout';
import { sso } from '../../../auth/sso';
import { headerMenuPaperStyles, userMenuTriggerStyles } from './StyledComponents';

// The auth site owns account management; this app only deep-links into it. The origin
// comes from the sso instance's already-normalised config, so the auth-site URL is read
// in exactly one place and trailing-slash handling cannot drift between callers.
//
// `return_to` is the parameter name the auth site already accepts on the logout handoff,
// and it carries a bare ORIGIN: the auth site matches it against an exact-origin
// allowlist, so a full deep path would fail that check.
export function buildAccountUrl() {
  const authSiteUrl = sso?.config?.authSiteUrl;
  if (!authSiteUrl) return null;
  return `${authSiteUrl}/account?return_to=${encodeURIComponent(window.location.origin)}`;
}

// Sign-out and account management live behind one trigger so there is a single place the
// app signs out from. Identity comes from UserContext, which only has a value once the
// access token has resolved — so this never renders a nameless pill.
export const UserMenu = () => {
  const user = useUser();
  const signOut = useSignout();
  const [anchorEl, setAnchorEl] = useState(null);
  const accountUrl = buildAccountUrl();

  const open = Boolean(anchorEl);
  const displayName = user?.displayName || user?.emailAddress || 'Account';
  const email = user?.emailAddress;
  // The trigger carries the address, not the display name: the address is the identity a
  // user recognises when checking which account a tab is signed into, and it is the value
  // every account action is keyed on. The display name still labels the control for
  // assistive tech, where the extra context costs no space.
  const triggerLabel = email || displayName;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Account management lives on a different origin, so this leaves the SPA entirely
  // rather than routing.
  const handleAccount = () => {
    handleClose();
    window.location.assign(accountUrl);
  };

  const handleSignOut = () => {
    handleClose();
    signOut();
  };

  return (
    <>
      <ButtonBase
        id="UserMenuTrigger"
        aria-label={`Account menu for ${displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'UserMenu' : undefined}
        onClick={handleOpen}
        sx={userMenuTriggerStyles}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}
        >
          <Typography
            component="span"
            sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.25 }}
            noWrap
          >
            {triggerLabel}
          </Typography>
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            flexShrink: 0,
            transition: 'transform 200ms',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </ButtonBase>
      <Menu
        id="UserMenu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { 'aria-labelledby': 'UserMenuTrigger' },
          paper: { sx: headerMenuPaperStyles },
        }}
      >
        {/* Withheld rather than rendered dead when the auth-site origin is unset. */}
        {accountUrl && (
          <MenuItem onClick={handleAccount}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            Account management
          </MenuItem>
        )}
        {accountUrl && <Divider />}
        <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};
